import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDailyReveals,
  dailyRevealQueryKeys,
  type DailyReveal,
} from '@/api/daily-reveals/getDailyReveals';
import { useGetTenantsQuery } from '@/api/tenants/getTenants';
import { useGetTenantImageLibraryQuery } from '@/api/tenants/tenantImageLibrary';
import { useGetPayoutTemplatesQuery } from '@/api/tenants/payoutTemplates';
import {
  useGetDailyRevealDaysQuery,
  useCreateDailyRevealDaysMutation,
  useUpdateDailyRevealDayMutation,
} from '@/api/daily-reveals/dailyRevealDays';
import { useGetGameLevelPacksQuery } from '@/api/daily-reveals/gameLevelPacks';
import { apiClient } from '@/api/api-client.config';
import { type RequestBody } from '@/api/react-query.config';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Label } from '@/components/ui/label';
import { TranslatableFieldLabel } from '@/components/composite/TranslatableFieldLabel';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  getDefaultDailyRevealModeForSport,
  parseAmericanOdds,
  getSportLogoUrl,
  getSportsForGameType,
  resolveDailyRevealMode,
} from '@low6-pickem/sports-config';
import { sportsForSelect } from '@/lib/platform-filters';
import { TzDateTimeInput } from '@/components/datetime/TzDateTimeInput';
import { TzTimeInput } from '@/components/datetime/TzTimeInput';
import { useTimezoneStore } from '@/stores/timezoneStore';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { createPickemValidationSchema } from '@/validation/pickemSchema';
import * as Yup from 'yup';
import { useFieldTranslation } from '@/hooks/useFieldTranslation';
import { toast } from 'sonner';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { PickemGoogleAd } from '@/components/pickems/PickemGoogleAd';
import { type GoogleAdsPlacement } from '@/api/tenants/googleAdsPlacements';
import {
  useGetPickemGoogleAdQuery,
  usePutPickemGoogleAdMutation,
} from '@/api/pickems/pickemGoogleAd';
import { DaysTab } from '@/components/daily-reveal/DaysTab';
import { EntriesTab } from '@/components/daily-reveal/EntriesTab';
import { ScoringTab } from '@/components/daily-reveal/ScoringTab';
import { DailyRevealGameRulesSection } from '@/components/daily-reveal/DailyRevealGameRulesSection';
import { DailyRevealStylingTab } from '@/components/daily-reveal/DailyRevealStylingTab';
import { defaultStyleConfig } from '@low6-pickem/style-config';
import {
  Calendar,
  Users,
  BarChart2,
  X,
  Info,
  ImagePlus,
  Search,
  RefreshCcw,
  Palette,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ImageUploadButton } from '@low6-pickem/ui';
import { uploadImage } from '@/api/upload';
import { convertUTCToTimezone } from '@/utils/timezone';

type CreatePickemRequest = RequestBody<'/api/admin/pickems', 'post'>;

interface DailyRevealFormValues {
  tenant_id: string;
  title_en: string;
  title_fr?: string;
  allow_selection_start?: string;
  sport: string;
  prize_text_en?: string;
  prize_text_fr?: string;
  text_prize_image_en?: string;
  daily_reveal_mode?: 'sport_default' | 'stat_hit' | 'prop_based';
  min_odds_american?: string;
  max_odds_american?: string;
  pickem_status?: string;
  payout_template_id?: string;
  game_duration?: number;
  days_start_date?: string;
  default_pack_id?: string;
  default_reveal_time?: string; // "HH:mm" UTC
  game_rules_version_id?: string;
  /** Always set (boolean) so Switch/inputs stay controlled; default false for new contests. */
  league_ip: boolean;
  /** Always set (boolean) so Switch stays controlled; default false for new contests. */
  lobby_hidden: boolean;
  style_config: Record<string, unknown>;
}

function getImageDisplayName(blobName: string) {
  const segments = blobName.split('/');
  return segments[segments.length - 1] || blobName;
}

function formatFileSize(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatAmericanOddsInput(value: number | null | undefined): string {
  if (value == null) return '';
  return value > 0 ? `+${value}` : String(value);
}

function normalizeAmericanOddsInput(
  value: string | number | null | undefined
): number | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  return parseAmericanOdds(trimmed);
}

function isInvalidAmericanOddsInput(rawValue: string | number | null | undefined): boolean {
  if (rawValue == null) return false;
  const trimmed = String(rawValue).trim();
  if (!trimmed) return false;
  return normalizeAmericanOddsInput(trimmed) == null;
}

export const Route = createFileRoute('/dashboard/daily-reveal/$id')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as string) || 'basic',
  }),
  // @ts-expect-error Inherited from main PR #476 — same loader type-
  // duplication issue around Pickem.style_config; see comment in
  // routes/dashboard/pickems/$id.tsx.
  loader: async ({ params }) => {
    if (params.id === 'new') return { contest: null };
    const { daily_reveals } = await getDailyReveals();
    const contest = daily_reveals.find(p => p.pickem_id === params.id);
    return { contest: contest || null };
  },
  component: DailyRevealEditPage,
});

function DailyRevealEditPage() {
  const { contest } = Route.useLoaderData() as { contest: DailyReveal | null };
  const { id: routeId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = routeId === 'new';
  const { timezone } = useTimezoneStore();
  const [currentTenantId, setCurrentTenantId] = useState<string | undefined>(contest?.tenant_id);
  const [bannerLibraryOpen, setBannerLibraryOpen] = useState(false);
  const [bannerLibrarySearch, setBannerLibrarySearch] = useState('');

  // Google Ad placement
  const { data: googleAdFromApi } = useGetPickemGoogleAdQuery(contest?.pickem_id);
  const { mutate: saveGoogleAd, isPending: isSavingGoogleAd } = usePutPickemGoogleAdMutation(
    contest?.pickem_id
  );
  const [googleAd, setGoogleAd] = useState<GoogleAdsPlacement | null>(null);

  useEffect(() => {
    if (googleAdFromApi) {
      setGoogleAd({ ...googleAdFromApi, placementKey: 'PICKEM_CONTEST_CARD' });
    } else {
      setGoogleAd(null);
    }
  }, [googleAdFromApi]);

  const { data: tenants = [], isLoading: tenantsLoading } = useGetTenantsQuery();
  const currentTenantCode = useMemo(
    () => tenants.find(tenant => tenant.tenant_id === currentTenantId)?.code,
    [tenants, currentTenantId]
  );
  const {
    data: tenantImageLibrary,
    isLoading: isTenantImageLibraryLoading,
    isFetching: isTenantImageLibraryFetching,
    isError: isTenantImageLibraryError,
    refetch: refetchTenantImageLibrary,
  } = useGetTenantImageLibraryQuery(currentTenantCode);

  const filteredTenantImages = useMemo(() => {
    const images = tenantImageLibrary?.images ?? [];
    const query = bannerLibrarySearch.trim().toLowerCase();
    if (!query) return images;
    return images.filter(image => {
      const name = getImageDisplayName(image.name).toLowerCase();
      return name.includes(query) || image.name.toLowerCase().includes(query);
    });
  }, [tenantImageLibrary?.images, bannerLibrarySearch]);
  const { data: payoutTemplatesData } = useGetPayoutTemplatesQuery(currentTenantId);
  const dailyRevealPayoutTemplates = useMemo(() => {
    return (payoutTemplatesData?.templates || []).filter(t => t.gameType === 'daily_reveal');
  }, [payoutTemplatesData]);

  const { translateField, isFieldTranslating } = useFieldTranslation();

  // Keep completed contests immutable, but allow updates for open/in-play contests
  // so admins can add or adjust basic info/rules after launch.
  const isEditable = !contest || contest.status !== 'COMPLETE';

  // Days data (only when editing)
  const { data: days = [], isLoading: daysLoading } = useGetDailyRevealDaysQuery(
    !isNew ? (contest?.pickem_id ?? '') : ''
  );
  const createDaysMutation = useCreateDailyRevealDaysMutation();
  const updateDayMutation = useUpdateDailyRevealDayMutation(contest?.pickem_id ?? '');

  // Global packs for dropdown in Days tab
  const { data: globalPacks = [] } = useGetGameLevelPacksQuery();

  const createMutation = useMutation({
    mutationFn: (
      data: CreatePickemRequest & { game_type?: string; game_rules_version_id?: string }
    ) => apiClient.post('/api/admin/pickems', data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dailyRevealQueryKeys.all });
    },
    onError: (error: { message?: string }) => {
      toast.error(error.message || 'Failed to create contest');
    },
  });

  type UpdatePickemRequest = RequestBody<'/api/admin/pickems/{id}', 'put'> & {
    game_rules_version_id?: string;
  };
  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: UpdatePickemRequest & { id: string }) =>
      apiClient.put('/api/admin/pickems/{id}', data as any, {
        pathParams: { id },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dailyRevealQueryKeys.all });
      toast.success('Daily Reveal contest updated');
    },
    onError: (error: { message?: string }) => {
      toast.error(error.message || 'Failed to update contest');
    },
  });

  const baseSchema = createPickemValidationSchema()
    .shape({
      start: Yup.string().optional(),
      daily_reveal_mode: Yup.string()
        .oneOf(['sport_default', 'stat_hit', 'prop_based'])
        .required('Gameplay mode is required'),
      min_odds_american: Yup.string()
        .nullable()
        .test(
          'valid-min-odds',
          'Minimum odds must be a valid American odds value such as +200 or -120.',
          value => !isInvalidAmericanOddsInput(value)
        ),
      max_odds_american: Yup.string()
        .nullable()
        .test(
          'valid-max-odds',
          'Maximum odds must be a valid American odds value such as +350 or +120.',
          value => !isInvalidAmericanOddsInput(value)
        ),
    })
    .test('odds-range-order', 'Minimum odds cannot be greater than maximum odds.', values => {
      const minOdds = normalizeAmericanOddsInput(values?.min_odds_american);
      const maxOdds = normalizeAmericanOddsInput(values?.max_odds_american);
      if (minOdds == null || maxOdds == null) return true;
      return minOdds <= maxOdds;
    });

  const validationSchema = isNew
    ? baseSchema.concat(
        Yup.object({
          game_duration: Yup.number()
            .required('Game duration is required')
            .min(1, 'Must be at least 1 day')
            .max(30, 'Must be 30 days or fewer'),
          payout_template_id: Yup.string().required('Payout template is required'),
        })
      )
    : baseSchema.concat(
        Yup.object({
          payout_template_id: Yup.string().required('Payout template is required'),
        })
      );

  const initialValues: DailyRevealFormValues = {
    tenant_id: contest?.tenant_id || '',
    title_en: contest?.title_en || '',
    title_fr: contest?.title_fr || '',
    allow_selection_start: contest?.allow_selection_start ?? '',
    sport: contest?.sport || 'MLB',
    prize_text_en: contest?.prize_text_en || '',
    prize_text_fr: contest?.prize_text_fr || '',
    text_prize_image_en: contest?.text_prize_image_en || '',
    daily_reveal_mode:
      (
        contest as
          | { daily_reveal_mode?: DailyRevealFormValues['daily_reveal_mode'] }
          | null
          | undefined
      )?.daily_reveal_mode || 'sport_default',
    min_odds_american: formatAmericanOddsInput(
      (contest as { min_odds_american?: number | null } | null | undefined)?.min_odds_american
    ),
    max_odds_american: formatAmericanOddsInput(
      (contest as { max_odds_american?: number | null } | null | undefined)?.max_odds_american
    ),
    pickem_status: contest?.status,
    payout_template_id: contest?.payout_template_id || undefined,
    game_duration: undefined,
    days_start_date: contest?.allow_selection_start
      ? contest.allow_selection_start.split('T')[0]
      : undefined,
    default_pack_id: undefined,
    default_reveal_time: '00:00',
    game_rules_version_id: contest?.game_rules_version_id || undefined,
    league_ip: contest?.league_ip === true,
    lobby_hidden: contest?.lobby_hidden === true,
    style_config:
      (contest as { style_config?: Record<string, unknown> } | null | undefined)?.style_config ??
      defaultStyleConfig('daily-reveal'),
  };

  const handleSubmit = async (
    values: DailyRevealFormValues,
    { setSubmitting }: { setSubmitting: (v: boolean) => void }
  ) => {
    try {
      const normalizedMinOdds = normalizeAmericanOddsInput(values.min_odds_american);
      const normalizedMaxOdds = normalizeAmericanOddsInput(values.max_odds_american);

      if (contest) {
        await updateMutation.mutateAsync({
          id: contest.pickem_id,
          tenant_id: values.tenant_id,
          title_en: values.title_en,
          title_fr: values.title_fr,
          allow_selection_start: values.allow_selection_start || undefined,
          sport: values.sport as CreatePickemRequest['sport'],
          prize_text_en: values.prize_text_en || '',
          prize_text_fr: values.prize_text_fr || '',
          text_prize_image_en: values.text_prize_image_en || undefined,
          daily_reveal_mode: values.daily_reveal_mode || 'sport_default',
          min_odds_american: normalizedMinOdds,
          max_odds_american: normalizedMaxOdds,
          payout_template_id: values.payout_template_id || undefined,
          game_rules_version_id: values.game_rules_version_id || undefined,
          league_ip: values.league_ip === true,
          lobby_hidden: values.lobby_hidden === true,
          style_config: values.style_config,
        });

        // Save Google Ad placement separately if it exists
        if (googleAd && contest.pickem_id) {
          await new Promise((resolve, reject) => {
            saveGoogleAd(googleAd, {
              onSuccess: resolve,
              onError: reject,
            });
          });
        }
      } else {
        const response = await createMutation.mutateAsync({
          tenant_id: values.tenant_id,
          title_en: values.title_en,
          title_fr: values.title_fr,
          allow_selection_start: values.allow_selection_start || undefined,
          sport: values.sport as CreatePickemRequest['sport'],
          prize_text_en: values.prize_text_en,
          prize_text_fr: values.prize_text_fr,
          text_prize_image_en: values.text_prize_image_en || undefined,
          daily_reveal_mode: values.daily_reveal_mode || 'sport_default',
          min_odds_american: normalizedMinOdds,
          max_odds_american: normalizedMaxOdds,
          payout_template_id: values.payout_template_id,
          game_type: 'daily_reveal',
          game_rules_version_id: values.game_rules_version_id || undefined,
          league_ip: values.league_ip === true,
          lobby_hidden: values.lobby_hidden === true,
        });
        const newPickemId = (response as { pickem: { pickem_id: string } }).pickem.pickem_id;

        // Auto-generate days if game_duration provided
        const gameDurationNum =
          typeof values.game_duration === 'number'
            ? values.game_duration
            : values.game_duration === '' || values.game_duration === undefined
              ? NaN
              : Number(values.game_duration);
        if (Number.isFinite(gameDurationNum) && gameDurationNum > 0) {
          const startDate = values.days_start_date || new Date().toISOString().split('T')[0];
          try {
            const daysResponse = await createDaysMutation.mutateAsync({
              gameId: newPickemId,
              gameDuration: gameDurationNum,
              startDate,
              defaultPackId: values.default_pack_id || undefined,
              defaultRevealTime: values.default_reveal_time || undefined,
            });
            toast.success('Daily Reveal contest created');
            if (daysResponse.autoSetup && !daysResponse.autoSetup.completed) {
              const failedDays = daysResponse.autoSetup.results.filter(
                result =>
                  result.fixtureSync === 'failed' ||
                  result.propSync === 'failed' ||
                  result.packGeneration === 'failed'
              );
              const warningMessage =
                failedDays[0]?.warnings[0] ||
                'Some days need manual attention in the Days tab before the contest is ready.';
              toast.warning(
                `Contest created, but automatic day setup only partially completed. ${warningMessage}`
              );
            }
          } catch {
            toast.warning(
              'Contest created, but failed to generate days. You can generate them from the Days tab.'
            );
          }
        } else {
          toast.success('Daily Reveal contest created');
        }

        navigate({
          to: '/dashboard/daily-reveal/$id',
          params: { id: newPickemId },
          search: { tab: 'basic' },
        });
      }
    } catch {
      // Error handled by mutation callbacks
    } finally {
      setSubmitting(false);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleBannerUpload = async (data: {
    path: string;
    filename: string;
    imageData: string;
  }) => {
    const result = await uploadImage(data);
    toast.success('Banner uploaded successfully');
    return { imageUrl: result.imageUrl };
  };

  return (
    <PageContainer>
      <Breadcrumbs
        overrides={{ 'daily-reveal': 'Daily Reveal', [routeId]: isNew ? 'New Contest' : 'Edit' }}
      />

      <div className="flex items-center gap-4 mb-6">
        <BackButton />
        <h1 className="text-2xl font-semibold">
          {isNew ? 'Create Daily Reveal Contest' : 'Edit Daily Reveal Contest'}
        </h1>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting }) => {
          const activeTab = search.tab || 'basic';
          const sportDefaultMode = getDefaultDailyRevealModeForSport(values.sport) ?? 'stat_hit';
          const resolvedMode = resolveDailyRevealMode(
            values.sport,
            values.daily_reveal_mode ?? 'sport_default'
          );

          return (
            <Form>
              <ContentCard>
                <div className="space-y-6 p-6">
                  <Tabs
                    value={activeTab}
                    onValueChange={value =>
                      navigate({
                        to: '/dashboard/daily-reveal/$id',
                        params: { id: routeId },
                        search: { tab: value },
                      })
                    }
                    className="w-full"
                  >
                    <TabsList className={`grid w-full ${contest ? 'grid-cols-6' : 'grid-cols-1'}`}>
                      <TabsTrigger value="basic">Basic Info</TabsTrigger>
                      {contest && (
                        <>
                          <TabsTrigger value="google-ad">Google Ad</TabsTrigger>
                          <TabsTrigger value="days">
                            <Calendar className="mr-2 h-4 w-4" />
                            Days ({days.length})
                          </TabsTrigger>
                          <TabsTrigger value="entries">
                            <Users className="mr-2 h-4 w-4" />
                            Entries
                          </TabsTrigger>
                          <TabsTrigger value="scoring">
                            <BarChart2 className="mr-2 h-4 w-4" />
                            Live Scoring
                          </TabsTrigger>
                          <TabsTrigger value="styling">
                            <Palette className="mr-2 h-4 w-4" />
                            Styling
                          </TabsTrigger>
                        </>
                      )}
                    </TabsList>

                    <TabsContent value="basic" className="space-y-4">
                      {/* Tenant + Sport */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <Label htmlFor="tenant_id">Tenant *</Label>
                          <Select
                            value={values.tenant_id || ''}
                            onValueChange={value => {
                              setFieldValue('tenant_id', value);
                              setFieldValue('payout_template_id', undefined);
                              setCurrentTenantId(value);
                            }}
                            disabled={tenantsLoading || !isEditable}
                          >
                            <SelectTrigger
                              className={
                                errors.tenant_id && touched.tenant_id ? 'border-red-500' : ''
                              }
                            >
                              <SelectValue placeholder="Select a tenant" />
                            </SelectTrigger>
                            <SelectContent>
                              {tenants.map(tenant => (
                                <SelectItem key={tenant.tenant_id} value={tenant.tenant_id}>
                                  {tenant.name_en || tenant.title || `Tenant #${tenant.tenant_id}`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <ErrorMessage name="tenant_id">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label htmlFor="sport">Sport</Label>
                          <Select
                            value={(values.sport as string) || 'NFL'}
                            onValueChange={value => {
                              setFieldValue('sport', value);
                              setFieldTouched('sport', true);
                            }}
                            disabled={!isEditable}
                          >
                            <SelectTrigger
                              className={
                                errors.sport && touched.sport
                                  ? 'border-red-500'
                                  : !isEditable
                                    ? 'bg-gray-50 cursor-not-allowed'
                                    : ''
                              }
                            >
                              <SelectValue placeholder="Select sport" />
                            </SelectTrigger>
                            <SelectContent>
                              {sportsForSelect(values.sport as string)
                                .filter(
                                  league =>
                                    getSportsForGameType('daily_reveal').includes(
                                      league.code as any
                                    ) || league.code === values.sport
                                )
                                .map(league => (
                                  <SelectItem key={league.code} value={league.code}>
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={getSportLogoUrl(league.code)}
                                        alt={league.code}
                                        className="w-6 h-6 object-contain"
                                      />
                                      <span>{league.filterLabel || league.name}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              <SelectItem value="NHL">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={getSportLogoUrl('NHL')}
                                    alt="NHL"
                                    className="w-6 h-6 object-contain"
                                  />
                                  <span>NHL</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="Mixed">
                                <div className="flex items-center gap-2">
                                  <img
                                    src={getSportLogoUrl('MIXED')}
                                    alt="Mixed"
                                    className="w-6 h-6 object-contain"
                                  />
                                  <span>Mixed</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <ErrorMessage name="sport">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Label htmlFor="daily_reveal_mode">Gameplay Mode</Label>
                        <Select
                          value={values.daily_reveal_mode || 'sport_default'}
                          onValueChange={value => {
                            setFieldValue('daily_reveal_mode', value);
                            setFieldTouched('daily_reveal_mode', true);
                          }}
                          disabled={!isEditable}
                        >
                          <SelectTrigger
                            className={
                              errors.daily_reveal_mode && touched.daily_reveal_mode
                                ? 'border-red-500'
                                : ''
                            }
                          >
                            <SelectValue placeholder="Select gameplay mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sport_default">
                              Use Sport Default (
                              {sportDefaultMode === 'prop_based' ? 'Prop-Based' : 'Stat-Hit'})
                            </SelectItem>
                            <SelectItem value="stat_hit">Stat-Hit</SelectItem>
                            <SelectItem value="prop_based">Prop-Based / Odds-Based</SelectItem>
                          </SelectContent>
                        </Select>
                        <ErrorMessage name="daily_reveal_mode">
                          {msg => <span className="text-sm text-red-500">{msg}</span>}
                        </ErrorMessage>
                        <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                          {values.daily_reveal_mode === 'sport_default'
                            ? `${values.sport} default: ${sportDefaultMode === 'prop_based' ? 'Prop-Based / Odds-Based' : 'Stat-Hit'}`
                            : resolvedMode === 'prop_based'
                              ? 'Users reveal player and prop combinations and are graded against a frozen threshold.'
                              : 'Users reveal player cards and are graded using the sport’s configured game statistic.'}
                        </div>
                      </div>

                      {resolvedMode === 'prop_based' && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col gap-2">
                            <Label htmlFor="min_odds_american">Minimum Odds</Label>
                            <Input
                              id="min_odds_american"
                              value={values.min_odds_american || ''}
                              onChange={e => setFieldValue('min_odds_american', e.target.value)}
                              onBlur={() => setFieldTouched('min_odds_american', true)}
                              placeholder="+200"
                              disabled={!isEditable}
                            />
                            <ErrorMessage name="min_odds_american">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>

                          <div className="flex flex-col gap-2">
                            <Label htmlFor="max_odds_american">Maximum Odds</Label>
                            <Input
                              id="max_odds_american"
                              value={values.max_odds_american || ''}
                              onChange={e => setFieldValue('max_odds_american', e.target.value)}
                              onBlur={() => setFieldTouched('max_odds_american', true)}
                              placeholder="+350"
                              disabled={!isEditable}
                            />
                            <ErrorMessage name="max_odds_american">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>
                        </div>
                      )}

                      {/* Payout Template + Default Pack Template */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <Label htmlFor="payout_template_id">Payout Template</Label>
                          <Select
                            value={values.payout_template_id || ''}
                            onValueChange={value => {
                              setFieldValue('payout_template_id', value || undefined);
                            }}
                            disabled={!values.tenant_id || !isEditable}
                          >
                            <SelectTrigger
                              className={!isEditable ? 'bg-gray-50 cursor-not-allowed' : ''}
                            >
                              <SelectValue
                                placeholder={
                                  !values.tenant_id
                                    ? 'Select a tenant first'
                                    : 'Select a payout template'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {dailyRevealPayoutTemplates.map(template => (
                                <SelectItem key={template.id} value={template.id}>
                                  {template.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <ErrorMessage name="payout_template_id">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                          {(() => {
                            const selectedTemplate = dailyRevealPayoutTemplates.find(
                              t => t.id === values.payout_template_id
                            );
                            if (!selectedTemplate) return null;
                            const maxTier = Math.max(...selectedTemplate.rows.map(r => r.rank));
                            const gameDays = isNew ? values.game_duration : days.length;
                            if (!gameDays || maxTier === gameDays) return null;
                            return (
                              <div className="rounded-md border border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-800">
                                <strong>Warning:</strong> This template has {maxTier} tiers but the
                                game has {gameDays} days. The top tier ({maxTier}+ qualifying days)
                                will be{' '}
                                {maxTier > gameDays
                                  ? 'impossible to reach — no one can qualify for the jackpot.'
                                  : 'easier to reach than intended.'}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Default Pack Template — only on create */}
                        {isNew && (
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-1">
                              <Label htmlFor="default_pack_id">Default Pack Template</Label>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger type="button">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Optional. Select a pack template to assign to all generated days
                                    by default.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Select
                              value={values.default_pack_id || 'none'}
                              onValueChange={value => {
                                setFieldValue(
                                  'default_pack_id',
                                  value === 'none' ? undefined : value
                                );
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="None (assign packs per day)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {globalPacks
                                  .filter(p => p.status === 'current' || p.status === 'draft')
                                  .map(pack => (
                                    <SelectItem key={pack.id} value={pack.id}>
                                      {pack.name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Open Date & Time — only on edit (create uses the row above) */}
                        {!isNew && (
                          <div className="flex flex-col gap-2">
                            <TzDateTimeInput
                              label="Open Date & Time"
                              id="allow_selection_start"
                              description="The date/time from when this contest is visible to users."
                              value={values.allow_selection_start ?? ''}
                              onChange={value => {
                                setFieldValue('allow_selection_start', value);
                              }}
                              disabled={!isEditable}
                              className={`${errors.allow_selection_start && touched.allow_selection_start ? 'border-red-500' : ''} ${!isEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            />
                            <ErrorMessage name="allow_selection_start">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id="league_ip"
                          checked={values.league_ip}
                          onCheckedChange={checked => setFieldValue('league_ip', checked)}
                          disabled={!isEditable}
                        />
                        <div className="flex items-center gap-1">
                          <Label htmlFor="league_ip" className="cursor-pointer">
                            League IP
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When enabled, this contest applies league IP eligibility rules. Off
                                by default.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Switch
                          id="lobby_hidden"
                          checked={values.lobby_hidden}
                          onCheckedChange={checked => setFieldValue('lobby_hidden', checked)}
                          disabled={!isEditable}
                        />
                        <div className="flex items-center gap-1">
                          <Label htmlFor="lobby_hidden" className="cursor-pointer">
                            Hide from Lobby
                          </Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-sm">
                                When on, this contest won't appear in the Lobby (sections, search or
                                featured rows) — players can only enter via its direct URL. Players
                                who have joined still see it in My Games. Off by default.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>

                      {/* Game Duration, First Day Date, Default Reveal Time, Open Date — only on create */}
                      {isNew && (
                        <div className="grid grid-cols-4 gap-4">
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-1">
                              <Label htmlFor="game_duration">Game Duration (days) *</Label>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger type="button">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Number of daily game days (1–30). Days are automatically
                                    generated starting from the Open Date.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Input
                              id="game_duration"
                              name="game_duration"
                              type="number"
                              min={1}
                              max={30}
                              placeholder="e.g. 7"
                              value={
                                values.game_duration === undefined || values.game_duration === null
                                  ? ''
                                  : values.game_duration
                              }
                              onChange={e => {
                                const v = e.target.value;
                                void setFieldValue(
                                  'game_duration',
                                  v === '' ? undefined : Number(v)
                                );
                              }}
                              onBlur={() => setFieldTouched('game_duration', true)}
                              className={
                                errors.game_duration && touched.game_duration
                                  ? 'border-red-500'
                                  : ''
                              }
                            />
                            <ErrorMessage name="game_duration">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>

                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-1">
                              <Label htmlFor="days_start_date">First Day Date</Label>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger type="button">
                                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    The date of Day 1. Defaults to the Open Date when set.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            <Input
                              id="days_start_date"
                              type="date"
                              value={values.days_start_date || ''}
                              onChange={e => setFieldValue('days_start_date', e.target.value)}
                            />
                          </div>

                          <TzTimeInput
                            label="Default Reveal Time"
                            id="default_reveal_time"
                            description="The time each day's reveal opens. Applied to all generated days and editable per day afterwards."
                            value={values.default_reveal_time}
                            onChange={value => setFieldValue('default_reveal_time', value)}
                          />

                          <div className="flex flex-col gap-2">
                            <TzDateTimeInput
                              label="Open Date & Time"
                              id="allow_selection_start"
                              description="The date/time from when this contest is visible to users."
                              value={values.allow_selection_start ?? ''}
                              onChange={value => {
                                setFieldValue('allow_selection_start', value);
                                if (value && !values.days_start_date) {
                                  setFieldValue(
                                    'days_start_date',
                                    convertUTCToTimezone(value, timezone, 'date')
                                  );
                                }
                              }}
                              disabled={!isEditable}
                              className={`${errors.allow_selection_start && touched.allow_selection_start ? 'border-red-500' : ''} ${!isEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            />
                            <ErrorMessage name="allow_selection_start">
                              {msg => <span className="text-sm text-red-500">{msg}</span>}
                            </ErrorMessage>
                          </div>
                        </div>
                      )}

                      {/* Title Fields */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                          <TranslatableFieldLabel
                            htmlFor="title_en"
                            onTranslate={() =>
                              translateField('title_en', values.title_en, 'title_fr', setFieldValue)
                            }
                            isTranslating={isFieldTranslating('title_en')}
                          >
                            Title (English)
                          </TranslatableFieldLabel>
                          <Field
                            as={Input}
                            id="title_en"
                            name="title_en"
                            placeholder="Enter contest title in English"
                            readOnly={!isEditable}
                            disabled={!isEditable}
                            className={`${errors.title_en && touched.title_en ? 'border-red-500' : ''} ${!isEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <ErrorMessage name="title_en">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>

                        <div className="flex flex-col gap-2">
                          <Label htmlFor="title_fr">Title (Spanish)</Label>
                          <Field
                            as={Input}
                            id="title_fr"
                            name="title_fr"
                            placeholder="Enter contest title in Spanish"
                            readOnly={isFieldTranslating('title_en') || !isEditable}
                            disabled={!isEditable}
                            className={`${errors.title_fr && touched.title_fr ? 'border-red-500' : ''} ${!isEditable ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                          />
                          <ErrorMessage name="title_fr">
                            {msg => <span className="text-sm text-red-500">{msg}</span>}
                          </ErrorMessage>
                        </div>
                      </div>

                      {/* Prize Text Fields — hidden per client request */}

                      {/* Jackpot Banner Image */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1">
                          <Label>Jackpot Banner</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger type="button">
                                <Info className="h-3.5 w-3.5 text-muted-foreground" />
                              </TooltipTrigger>
                              <TooltipContent>
                                Banner image displayed at the top of the Daily Reveal game.
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        {values.text_prize_image_en && (
                          <div className="relative w-fit">
                            <img
                              src={values.text_prize_image_en}
                              alt="Jackpot banner preview"
                              className="max-w-xs rounded-lg border object-contain"
                              onError={e => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setFieldValue('text_prize_image_en', '')}
                              className="absolute top-1 right-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                              aria-label="Remove banner image"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )}

                        <ImageUploadButton
                          onUpload={url => {
                            setFieldValue('text_prize_image_en', url);
                          }}
                          path="daily-reveal/banners"
                          filename="jackpot-banner"
                          uploadFn={handleBannerUpload}
                          onError={message => toast.error(message)}
                          title="Upload Banner"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="w-fit"
                          disabled={!isEditable || !values.tenant_id || !currentTenantCode}
                          onClick={() => setBannerLibraryOpen(true)}
                          title={
                            values.tenant_id
                              ? 'Select from tenant image library'
                              : 'Select a tenant first to use image library'
                          }
                        >
                          <ImagePlus className="h-4 w-4 mr-2" />
                          Select from Image Library
                        </Button>
                        <p className="text-xs text-muted-foreground">
                          Use manual upload or choose an existing tenant image from Image Library.
                        </p>
                      </div>

                      {/* Status (read-only when editing) */}
                      {contest && (
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-1">
                            <Label htmlFor="pickem_status">Status</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger type="button">
                                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  Status is automatically managed: DRAFT → SCHEDULED → OPEN → INPLAY
                                  → COMPLETE
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <Input
                            id="pickem_status"
                            value={(values.pickem_status || 'DRAFT') as string}
                            readOnly
                            disabled
                            className="bg-gray-50 cursor-not-allowed"
                          />
                        </div>
                      )}

                      {contest && <DailyRevealGameRulesSection disabled={!isEditable} />}
                    </TabsContent>

                    {contest && (
                      <>
                        <TabsContent value="google-ad" className="space-y-4">
                          <PickemGoogleAd
                            pickem={{ ...contest, google_ad: googleAd } as any}
                            placement={googleAd}
                            onPlacementChange={setGoogleAd}
                            isSaving={isSubmitting || isPending || isSavingGoogleAd}
                          />
                        </TabsContent>
                        <TabsContent value="days">
                          <DaysTab
                            gameId={contest!.pickem_id}
                            days={days}
                            isLoading={daysLoading}
                            globalPacks={globalPacks}
                            sport={values.sport || contest?.sport || 'NFL'}
                            resolvedMode={resolvedMode}
                            contestMinOdds={
                              normalizeAmericanOddsInput(values.min_odds_american) ?? null
                            }
                            contestMaxOdds={
                              normalizeAmericanOddsInput(values.max_odds_american) ?? null
                            }
                            onUpdateDay={(dayId, data) =>
                              updateDayMutation.mutateAsync({ dayId, ...data })
                            }
                          />
                        </TabsContent>
                        <TabsContent value="entries">
                          <EntriesTab gameId={contest!.pickem_id} />
                        </TabsContent>
                        <TabsContent value="scoring">
                          <ScoringTab gameId={contest!.pickem_id} days={days} />
                        </TabsContent>
                        <TabsContent value="styling" className="space-y-4">
                          <DailyRevealStylingTab
                            pickemId={contest.pickem_id}
                            isDefaultStyle={
                              (contest as { is_default_style?: boolean }).is_default_style ?? false
                            }
                            titleEn={contest.title_en}
                            tenantId={contest.tenant_id}
                            tenantCode={currentTenantCode}
                            disabled={!isEditable}
                          />
                          {!isEditable && (
                            <Button
                              type="button"
                              onClick={() =>
                                updateMutation.mutate({
                                  id: contest.pickem_id,
                                  tenant_id: values.tenant_id || contest.tenant_id || '',
                                  title_en: values.title_en || contest.title_en,
                                  title_fr: values.title_fr,
                                  allow_selection_start: values.allow_selection_start,
                                  sport: values.sport as CreatePickemRequest['sport'],
                                  prize_text_en: values.prize_text_en || '',
                                  prize_text_fr: values.prize_text_fr,
                                  text_prize_image_en: values.text_prize_image_en || undefined,
                                  daily_reveal_mode: values.daily_reveal_mode || 'sport_default',
                                  min_odds_american: normalizeAmericanOddsInput(
                                    values.min_odds_american
                                  ),
                                  max_odds_american: normalizeAmericanOddsInput(
                                    values.max_odds_american
                                  ),
                                  payout_template_id: values.payout_template_id || undefined,
                                  game_rules_version_id: values.game_rules_version_id || undefined,
                                  league_ip: values.league_ip === true,
                                  style_config: values.style_config,
                                })
                              }
                              disabled={isPending}
                              className="w-full"
                            >
                              {isPending ? 'Saving...' : 'Save Styling'}
                            </Button>
                          )}
                        </TabsContent>
                      </>
                    )}
                  </Tabs>

                  <Dialog
                    open={bannerLibraryOpen}
                    onOpenChange={open => {
                      setBannerLibraryOpen(open);
                      if (!open) {
                        setBannerLibrarySearch('');
                      }
                    }}
                  >
                    <DialogContent className="max-w-5xl p-0">
                      <DialogHeader className="px-6 pt-6">
                        <DialogTitle>Select Jackpot Banner from Image Library</DialogTitle>
                        <DialogDescription>
                          Choose an image from tenant image library for this Daily Reveal jackpot
                          banner.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="px-6 pb-6 space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              value={bannerLibrarySearch}
                              onChange={e => setBannerLibrarySearch(e.target.value)}
                              placeholder="Search images by filename..."
                              className="pl-9"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => refetchTenantImageLibrary()}
                            disabled={isTenantImageLibraryFetching}
                          >
                            <RefreshCcw
                              className={
                                isTenantImageLibraryFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'
                              }
                            />
                            Refresh
                          </Button>
                        </div>

                        <div className="max-h-[58vh] overflow-y-auto border rounded-md p-3">
                          {isTenantImageLibraryLoading ? (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                              Loading image library...
                            </div>
                          ) : isTenantImageLibraryError ? (
                            <div className="py-10 text-center text-sm text-red-600">
                              Failed to load image library.
                            </div>
                          ) : filteredTenantImages.length === 0 ? (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                              {(tenantImageLibrary?.images?.length ?? 0) === 0
                                ? 'No images found in this tenant image library.'
                                : 'No images match your search.'}
                            </div>
                          ) : (
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {filteredTenantImages.map(image => {
                                const displayName = getImageDisplayName(image.name);
                                return (
                                  <button
                                    key={image.name}
                                    type="button"
                                    className="text-left border rounded-md p-2 hover:border-primary transition-colors"
                                    onClick={() => {
                                      setFieldValue('text_prize_image_en', image.url);
                                      setBannerLibraryOpen(false);
                                      setBannerLibrarySearch('');
                                    }}
                                  >
                                    <div className="h-28 w-full overflow-hidden rounded border bg-muted/20 mb-2">
                                      <img
                                        src={image.url}
                                        alt={displayName}
                                        className="h-full w-full object-contain"
                                        loading="lazy"
                                      />
                                    </div>
                                    <div
                                      className="text-xs font-medium truncate"
                                      title={displayName}
                                    >
                                      {displayName}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      {formatFileSize(image.size)}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Save Button — Basic Info, Google Ad, and Styling tabs */}
                  {activeTab === 'basic' ||
                  activeTab === 'google-ad' ||
                  (activeTab === 'styling' && isEditable) ? (
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        type="button"
                        variant="outline"
                        className="mr-2"
                        onClick={() => navigate({ to: '/dashboard/daily-reveal' })}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isSubmitting || isPending}>
                        {isPending ? 'Saving...' : isNew ? 'Create Contest' : 'Save Changes'}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </ContentCard>
            </Form>
          );
        }}
      </Formik>
    </PageContainer>
  );
}
